import { Component, Suspense, type ErrorInfo, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link, Route, Routes } from "react-router-dom";
import { spaRoutes } from "./routes/route-definitions";
import { MobileShellRedirectGate } from "./mobile/MobileShellRedirectGate";
import { RouteLoadingFallback } from "./components/RouteLoadingFallback";
import { SessionExpiredDialog } from "./components/SessionExpiredDialog";

type SpaErrorBoundaryProps = {
  children: ReactNode;
  fallback: ReactNode;
};

type SpaErrorBoundaryState = {
  hasError: boolean;
};

class SpaErrorBoundary extends Component<SpaErrorBoundaryProps, SpaErrorBoundaryState> {
  state: SpaErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): SpaErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("SPA unhandled render error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

function GlobalSpaErrorBoundary({ children }: { children: ReactNode }) {
  const { t } = useTranslation("auth");
  return (
    <SpaErrorBoundary
      fallback={(
        <main className="spa-public-shell">
          <section className="spa-panel">
            <span className="tag">{t("globalError.tag")}</span>
            <h1>{t("globalError.title")}</h1>
            <p className="spa-alert is-error">{t("globalError.description")}</p>
            <div className="spa-card-actions">
              <button className="btn" type="button" onClick={() => window.location.reload()}>{t("globalError.reload")}</button>
              <Link className="spa-public-nav-link" to="/login">{t("globalError.login")}</Link>
            </div>
          </section>
        </main>
      )}
    >
      {children}
    </SpaErrorBoundary>
  );
}

export function SpaApp() {
  return (
    <GlobalSpaErrorBoundary>
      <MobileShellRedirectGate />
      {/* 会话失效是全局事件：哪个页面撞上 401 都由这一个弹窗接管，不再各页自行铺错误卡片。 */}
      <SessionExpiredDialog />
      <Suspense fallback={<RouteLoadingFallback />}>
        <Routes>
          {spaRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
        </Routes>
      </Suspense>
    </GlobalSpaErrorBoundary>
  );
}
